/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { matchesChannelId } from './helpers/channelHelpers';
import { findListingEntity, listListingVariantsForChannel } from './helpers/entityReadHelpers';
import { removeListingVariant, upsertListing, upsertListingVariant } from './helpers/entityAdapter';
import {
  hasChannelProductGoneFlag,
  hasChannelVariantGoneFlag,
  showChannelVariantGoneToast,
} from './helpers/showChannelProductGoneToast';

export const getListingVariants = createAsyncThunk(
  'products/getListingVariants',
  async ({ masterProductId, channelId, listingProductId }, { extra: { Logger, Toast, sdk } }) => {
    try {
      const { data: variants } = await sdk.products.getVariants(listingProductId);

      // Product-gone toast is owned by getListings; only surface variant-gone here.
      if (
        hasChannelVariantGoneFlag(variants)
        && !hasChannelProductGoneFlag(variants)
      ) {
        showChannelVariantGoneToast({ Toast });
      }

      return { masterProductId, channelId, variants };
    } catch (error) {
      Logger.error(new Error('Error getting listing variants'), { error }, [
        'GetListingVariantsFailed',
        { errorMessage: 'Cannot load variants for listing' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(getListingVariants.fulfilled, (state, { payload }) => {
      const { masterProductId, channelId, variants } = payload;
      const listing = findListingEntity(state, { masterProductId, channelId });

      if (!listing?._id) return;

      const safeVariants = variants || [];

      listListingVariantsForChannel(state, masterProductId, channelId).forEach((channelEntry) => {
        if (matchesChannelId(channelEntry, channelId) && channelEntry._id) {
          removeListingVariant(state, channelEntry._id);
        }
      });

      safeVariants.forEach((listingVariant) => {
        const variantWithoutGoneFlags = { ...listingVariant };
        delete variantWithoutGoneFlags.channelProductGone;
        delete variantWithoutGoneFlags.channelVariantGone;
        delete variantWithoutGoneFlags.liveStale;
        upsertListingVariant(state, variantWithoutGoneFlags);
      });

      if (listing.live) {
        upsertListing(state, {
          ...listing,
          masterProductId,
          live: {
            ...listing.live,
            variants: safeVariants
              .filter((variant) => variant.live)
              .map((variant) => ({
                ...variant.live,
                _id: variant._id,
                masterVariantId: variant.masterVariantId,
                title: variant.live.title || variant.title,
              })),
          },
        });
      }
    });
};
